import { useEffect, useState } from "react";
import { Wrench, BookMarked, ShieldCheck } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Renders an Under Construction screen for non-admins when maintenance mode is on.
 * Admins see a sticky banner instead and can keep using the app to toggle it off.
 * Always allows /login and /auth callback so admins can sign in.
 */
export default function MaintenanceGate({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState(null); // { enabled, message }

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const r = await api.get("/maintenance/status");
        if (!cancelled) setStatus(r.data);
      } catch {
        if (!cancelled) setStatus({ enabled: false, message: "" });
      }
    };
    fetchStatus();
    // Re-check every 60s so admins toggling off don't have to refresh.
    const id = setInterval(fetchStatus, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [location.pathname]);

  if (loading || !status) return children;

  const isLoginRoute = location.pathname === "/login" || location.hash?.includes("session_id=");

  // Maintenance OFF — render app normally.
  if (!status.enabled) return children;

  // Admin — show sticky banner + render app so they can toggle off.
  if (user?.is_admin) {
    return (
      <>
        <div
          data-testid="maintenance-admin-banner"
          className="bg-yellow-300 border-b-2 border-slate-900 px-4 py-2 flex flex-wrap items-center justify-center gap-3 text-xs sm:text-sm font-bold md:sticky md:top-0 md:z-40 relative z-10 md:mt-0 mt-14"
        >
          <Wrench className="w-4 h-4 flex-shrink-0" strokeWidth={2.5}/>
          <span className="leading-tight text-center">Maintenance mode is ON — site hidden from regular users.</span>
          <Link to="/admin/maintenance" className="underline hover:no-underline whitespace-nowrap">Manage</Link>
        </div>
        {children}
      </>
    );
  }

  // Non-admin — always allow login so admins can sign in. Otherwise block.
  if (isLoginRoute) return children;

  return (
    <div className="min-h-screen bg-yellow-50 flex items-center justify-center p-6">
      <div className="nb-card p-8 max-w-md w-full text-center bg-white">
        <div className="w-14 h-14 mx-auto bg-yellow-300 border-2 border-slate-900 rounded-md grid place-items-center" style={{ boxShadow: "3px 3px 0 0 #0F172A" }}>
          <Wrench className="w-7 h-7" strokeWidth={2.5}/>
        </div>
        <h1 className="font-display text-3xl font-black mt-4">Under construction</h1>
        <p className="text-sm font-medium text-slate-700 mt-3 whitespace-pre-line" data-testid="maintenance-message">
          {status.message}
        </p>
        <div className="mt-6 flex items-center justify-center gap-2 text-xs font-bold text-slate-500">
          <BookMarked className="w-4 h-4" strokeWidth={2.5}/> Studict will be back soon.
        </div>
        <Link
          to="/login"
          data-testid="maintenance-admin-login"
          className="mt-6 inline-flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-slate-900 underline"
        >
          <ShieldCheck className="w-3 h-3" strokeWidth={2.5}/> Admin sign-in
        </Link>
      </div>
    </div>
  );
}
